/**
 * Shared chrome for the Director pages (composer home + workspace).
 * Exports theme vars, the 56px global nav, and the 52px top bar so both
 * pages look identical except for main-content area.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import {
  Sparkles,
  Music as MusicIcon,
  Mic as MicIcon,
  Video as VideoIcon,
  Activity as WaveIcon,
  Layers as LayersIcon,
  List as ListIcon,
  Play as PlayIcon,
  HelpCircle,
  Globe,
  Bell,
  Crown,
  FolderOpen,
  LogOut,
  ReceiptText,
  Settings,
  ShoppingBag,
  Star,
  User as UserIcon,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import CreditHistoryModal from "@/components/CreditHistoryModal";
import FeedbackDialog from "@/components/FeedbackDialog";
import DiscordButton from "@/components/DiscordButton";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";
import { languageNames } from "@/locales";
import { getPlanTierIndex } from "@/lib/pricingPlans";

export const THEME_VARS = {
  "--bg": "#0b0b10",
  "--bg-2": "#111119",
  "--panel": "#16161f",
  "--panel-2": "#1d1d28",
  "--panel-3": "#24242f",
  "--line": "rgba(255,255,255,0.06)",
  "--line-2": "rgba(255,255,255,0.10)",
  "--fg": "#ececf1",
  "--fg-dim": "#9a9aa8",
  "--fg-mute": "#5f5f6e",
  "--accent": "#a78bfa",
  "--accent-2": "#8b5cf6",
  "--accent-soft": "rgba(167,139,250,0.14)",
  "--green": "#10b981",
  "--amber": "#f59e0b",
  "--pink": "#ec4899",
  "--serif": "'Instrument Serif', serif",
  "--mono": "'JetBrains Mono', ui-monospace, monospace",
  "--sans": "'Inter', system-ui, sans-serif",
} as CSSProperties;

const CHROME_COPY: Record<string, { help: string; feedback: string; openFeedback: string }> = {
  en: { help: "Help", feedback: "Feedback", openFeedback: "Open feedback form" },
  es: { help: "Ayuda", feedback: "Comentarios", openFeedback: "Abrir formulario de comentarios" },
  ja: { help: "ヘルプ", feedback: "フィードバック", openFeedback: "フィードバックフォームを開く" },
  pt: { help: "Ajuda", feedback: "Feedback", openFeedback: "Abrir formulário de feedback" },
  fr: { help: "Aide", feedback: "Retour", openFeedback: "Ouvrir le formulaire de retour" },
  zh: { help: "帮助", feedback: "反馈", openFeedback: "打开反馈表单" },
  ko: { help: "도움말", feedback: "피드백", openFeedback: "피드백 양식 열기" },
  id: { help: "Bantuan", feedback: "Masukan", openFeedback: "Buka formulir masukan" },
  it: { help: "Aiuto", feedback: "Feedback", openFeedback: "Apri modulo feedback" },
  nl: { help: "Hulp", feedback: "Feedback", openFeedback: "Feedbackformulier openen" },
  pl: { help: "Pomoc", feedback: "Opinie", openFeedback: "Otwórz formularz opinii" },
  tr: { help: "Yardım", feedback: "Geri bildirim", openFeedback: "Geri bildirim formunu aç" },
  vi: { help: "Trợ giúp", feedback: "Phản hồi", openFeedback: "Mở biểu mẫu phản hồi" },
  de: { help: "Hilfe", feedback: "Feedback", openFeedback: "Feedbackformular öffnen" },
};

function chromeCopyFor(lang: string) {
  return CHROME_COPY[lang.split("-")[0]] ?? CHROME_COPY.en;
}

const SUPPORTED_LANGS = new Set([
  "en",
  "ko",
  "ja",
  "zh",
  "es",
  "pt",
  "fr",
  "de",
  "it",
  "ru",
  "ar",
  "hi",
  "id",
  "vi",
  "th",
  "tr",
  "pl",
  "nl",
]);

function stripLang(path: string): string {
  const parts = path.split("/").filter(Boolean);
  if (parts[0] && SUPPORTED_LANGS.has(parts[0])) {
    return "/" + parts.slice(1).join("/");
  }
  return path || "/";
}

function isItemActive(path: string, href: string): boolean {
  const p = stripLang(path.split(/[?#]/)[0]);
  if (href === "/songs") {
    return p === "/songs" || p.startsWith("/song/");
  }
  if (href === "/playlists") {
    return p === "/playlists" || p.startsWith("/playlist/");
  }
  if (href === "/ai-music-video-generator") {
    return (
      p === "/ai-music-video-generator" ||
      p.startsWith("/ai-music-video-generator/") ||
      p.startsWith("/ai-music-video/")
    );
  }
  if (href === "/ai-music-generator") {
    if (
      p === "/ai-music-video-generator" ||
      p.startsWith("/ai-music-video-generator/") ||
      p.startsWith("/ai-music-video/")
    ) {
      return false;
    }
    return (
      p === "/ai-music-generator" ||
      p === "/vibes" ||
      p.endsWith("-song-generator") ||
      (p.endsWith("-music-generator") && p !== "/ai-music-video-generator") ||
      p.endsWith("-generator")
    );
  }
  if (href === "/ai-cover") {
    return (
      p === "/ai-cover" ||
      p.startsWith("/ai-cover/") ||
      p === "/ai-music-cover" ||
      p === "/custom-voice"
    );
  }
  return p === href;
}

function isMusicVideoProjectPage(path: string): boolean {
  const p = stripLang(path.split(/[?#]/)[0]);
  const parts = p.split("/").filter(Boolean);
  return (
    (parts[0] === "ai-music-video" ||
      (parts[0] === "ai-music-video-generator" && parts.length > 1)) &&
    Boolean(parts[1])
  );
}

const DIRECTOR_MOBILE_QUERY = "(max-width: 1199px)";
const GLOBAL_NAV_COLLAPSED_KEY = "solmi-global-nav-collapsed";

export function useDirectorMobileViewport() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia(DIRECTOR_MOBILE_QUERY).matches,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia(DIRECTOR_MOBILE_QUERY);
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isMobile;
}

export function GlobalNav() {
  const { t } = useTranslation();
  const [location] = useLocation();
  const { withLanguagePath, currentLang } = useLanguageRouter();
  const chrome = chromeCopyFor(currentLang);
  const isMobile = useDirectorMobileViewport();
  const isProjectPage = isMusicVideoProjectPage(location);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    if (isMusicVideoProjectPage(window.location.pathname)) return true;
    return window.localStorage.getItem(GLOBAL_NAV_COLLAPSED_KEY) === "true";
  });
  const items: {
    icon: LucideIcon;
    label: string;
    href: string;
  }[] = [
    {
      icon: Sparkles,
      label: t("nav.featured", { defaultValue: "Featured" }),
      href: "/featured",
    },
    {
      icon: MusicIcon,
      label: t("globalNav.nav.createSong", { defaultValue: "Create Song" }),
      href: "/ai-music-generator",
    },
    {
      icon: VideoIcon,
      label: t("globalNav.nav.createMusicVideo", {
        defaultValue: "Create Music Video",
      }),
      href: "/ai-music-video-generator",
    },
    {
      icon: MicIcon,
      label: t("library.createButtons.createCover", {
        defaultValue: "Create Cover",
      }),
      href: "/ai-cover",
    },
    {
      icon: LayersIcon,
      label: t("globalNav.tools.stemSplitter", {
        defaultValue: "Stem Splitter",
      }),
      href: "/stem-splitter",
    },
    {
      icon: ListIcon,
      label: t("globalNav.nav.library", { defaultValue: "Library" }),
      href: "/songs",
    },
    {
      icon: FolderOpen,
      label: t("nav.projects", { defaultValue: "Projects" }),
      href: "/projects",
    },
    {
      icon: WaveIcon,
      label: t("nav.vocalRemover", {
        defaultValue: t("globalNav.tools.vocalRemover", {
          defaultValue: "Vocal Remover",
        }),
      }),
      href: "/vocal-remover",
    },
    {
      icon: PlayIcon,
      label: t("nav.playlists", { defaultValue: "Playlists" }),
      href: "/playlists",
    },
  ];

  useEffect(() => {
    if (typeof window === "undefined" || isMobile || isProjectPage) return;
    window.localStorage.setItem(GLOBAL_NAV_COLLAPSED_KEY, String(collapsed));
  }, [collapsed, isMobile, isProjectPage]);

  useEffect(() => {
    if (isMobile) return;
    if (isProjectPage) {
      setCollapsed(true);
      return;
    }
    if (typeof window === "undefined") return;
    setCollapsed(
      window.localStorage.getItem(GLOBAL_NAV_COLLAPSED_KEY) === "true",
    );
  }, [isMobile, isProjectPage, location]);

  if (isMobile) {
    return (
      <nav
        aria-label="Primary"
        style={{
          ...THEME_VARS,
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 80,
          height: "calc(58px + env(safe-area-inset-bottom))",
          padding:
            "6px max(10px, env(safe-area-inset-left)) calc(6px + env(safe-area-inset-bottom)) max(10px, env(safe-area-inset-right))",
          background: "rgba(0,0,0,0.94)",
          borderTop: "1px solid var(--line-2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          gap: 4,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {items.map((it, i) => {
          const I = it.icon;
          const active = isItemActive(location, it.href);
          return (
            <Link key={i} href={withLanguagePath(it.href)}>
              <span
                title={it.label}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  display: "grid",
                  placeItems: "center",
                  background: active
                    ? "rgba(167,139,250,0.16)"
                    : "transparent",
                  color: active ? "#c4b5fd" : "var(--fg-mute)",
                  border: active
                    ? "1px solid rgba(167,139,250,0.35)"
                    : "1px solid transparent",
                  position: "relative",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <I size={18} />
                {active && (
                  <span
                    style={{
                      position: "absolute",
                      left: 10,
                      right: 10,
                      bottom: 3,
                      height: 2,
                      background: "var(--accent)",
                      borderRadius: 2,
                    }}
                  />
                )}
              </span>
            </Link>
          );
        })}
      </nav>
    );
  }

  return (
    <div
      style={{
        ...THEME_VARS,
        width: collapsed ? 56 : 232,
        background: "#000",
        borderRight: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        alignItems: collapsed ? "center" : "stretch",
        padding: collapsed ? "14px 0 10px" : "14px 12px 12px",
        gap: 2,
        flexShrink: 0,
        position: "sticky",
        top: 0,
        height: "100vh",
        zIndex: 40,
        transition: "width 180ms ease, padding 180ms ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          gap: 8,
          minHeight: 38,
          marginBottom: collapsed ? 10 : 12,
        }}
      >
        <Link href={withLanguagePath("/")}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
              color: "var(--fg)",
              textDecoration: "none",
              cursor: "pointer",
            }}
            title="Solmi"
          >
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: "#000",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src="/solmi/solmi-logo-emerald.png"
                alt="Solmi"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </span>
            {!collapsed && (
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: 0,
                  whiteSpace: "nowrap",
                }}
              >
                Solmi
              </span>
            )}
          </span>
        </Link>
        {!collapsed && (
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            style={{
              width: 32,
              height: 32,
              border: "1px solid var(--line)",
              borderRadius: 8,
              display: "grid",
              placeItems: "center",
              color: "var(--fg-dim)",
              background: "rgba(255,255,255,0.03)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <ChevronLeft size={17} />
          </button>
        )}
      </div>
      <div
        style={{
          width: collapsed ? 24 : "100%",
          height: 1,
          background: "var(--line)",
          margin: collapsed ? "2px 0 8px" : "0 0 10px",
        }}
      />
      {items.map((it, i) => {
        const I = it.icon;
        const active = isItemActive(location, it.href);
        return (
          <Link key={i} href={withLanguagePath(it.href)}>
            <span
              title={it.label}
              style={{
                width: collapsed ? 40 : "100%",
                height: 40,
                borderRadius: 9,
                display: "flex",
                alignItems: "center",
                justifyContent: collapsed ? "center" : "flex-start",
                gap: collapsed ? 0 : 12,
                padding: collapsed ? 0 : "0 12px",
                background: active ? "rgba(167,139,250,0.14)" : "transparent",
                color: active ? "#c4b5fd" : "var(--fg-mute)",
                border: active
                  ? "1px solid rgba(167,139,250,0.3)"
                  : "1px solid transparent",
                position: "relative",
                cursor: "pointer",
                textDecoration: "none",
                boxSizing: "border-box",
                marginBottom: 2,
                transition:
                  "background 160ms ease, border-color 160ms ease, color 160ms ease",
              }}
            >
              <I size={17} style={{ flexShrink: 0 }} />
              {!collapsed && (
                <span
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 14,
                    fontWeight: active ? 650 : 500,
                    letterSpacing: 0,
                  }}
                >
                  {it.label}
                </span>
              )}
              {active && (
                <span
                  style={{
                    position: "absolute",
                    left: collapsed ? -1 : 0,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    background: "var(--accent)",
                    borderRadius: 2,
                  }}
                />
              )}
            </span>
          </Link>
        );
      })}
      <div style={{ flex: 1 }} />
      {collapsed && (
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label="Expand sidebar"
          title="Expand sidebar"
          style={{
            width: 40,
            height: 40,
            border: "1px solid transparent",
            borderRadius: 9,
            display: "grid",
            placeItems: "center",
            color: "var(--fg-mute)",
            background: "transparent",
            cursor: "pointer",
            marginBottom: 4,
          }}
        >
          <ChevronRight size={17} />
        </button>
      )}
      <Link href={withLanguagePath("/help")}>
        <span
          title={chrome.help}
          style={{
            width: collapsed ? 40 : "100%",
            height: 40,
            borderRadius: 9,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: collapsed ? 0 : 12,
            padding: collapsed ? 0 : "0 12px",
            boxSizing: "border-box",
            color: "var(--fg-mute)",
            cursor: "pointer",
            textDecoration: "none",
          }}
        >
          <HelpCircle size={17} style={{ flexShrink: 0 }} />
          {!collapsed && (
            <span
              style={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: 0,
              }}
            >
              {chrome.help}
            </span>
          )}
        </span>
      </Link>
    </div>
  );
}

export function TopBar({
  title,
  projectTitle,
  credits,
  musicVideoCredits,
  plan,
  userName,
  userImage,
  rightSlot,
  onSignIn,
  onUpgradeClick,
}: {
  title: string;
  projectTitle?: string;
  credits: number | null;
  musicVideoCredits?: number | null;
  plan?: string | null;
  userName: string | null;
  userImage: string | null;
  rightSlot?: ReactNode;
  onSignIn?: () => void;
  onUpgradeClick?: (intent: "upgrade" | "topup") => void;
}) {
  const isMobile = useDirectorMobileViewport();

  return (
    <div
      style={{
        height: isMobile ? 56 : 52,
        display: "flex",
        alignItems: "center",
        padding: isMobile ? "0 12px" : "0 20px",
        borderBottom: "1px solid var(--line)",
        background: "var(--bg)",
        gap: isMobile ? 8 : 14,
        flexShrink: 0,
        position: isMobile ? "sticky" : undefined,
        top: isMobile ? 0 : undefined,
        zIndex: isMobile ? 50 : undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flex: 1,
          minWidth: 0,
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 7,
            background: "var(--accent-soft)",
            display: "grid",
            placeItems: "center",
            color: "var(--accent)",
            flexShrink: 0,
          }}
        >
          <VideoIcon size={14} />
        </div>
        <span
          style={{
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {title}
        </span>
        {projectTitle && !isMobile && (
          <>
            <span style={{ color: "var(--fg-mute)", margin: "0 4px" }}>|</span>
            <span
              style={{
                color: "var(--fg-dim)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {projectTitle}
            </span>
          </>
        )}
      </div>
      <TopBarControls
        credits={credits}
        musicVideoCredits={musicVideoCredits}
        plan={plan}
        userName={userName}
        userImage={userImage}
        rightSlot={rightSlot}
        onSignIn={onSignIn}
        onUpgradeClick={onUpgradeClick}
      />
    </div>
  );
}

function TopBarControls({
  credits,
  musicVideoCredits,
  plan,
  userName,
  userImage,
  rightSlot,
  onSignIn,
  onUpgradeClick,
}: {
  credits: number | null;
  musicVideoCredits?: number | null;
  plan?: string | null;
  userName: string | null;
  userImage: string | null;
  rightSlot?: ReactNode;
  onSignIn?: () => void;
  onUpgradeClick?: (intent: "upgrade" | "topup") => void;
}) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { withLanguagePath, currentLang, changeLanguage } = useLanguageRouter();
  const [, navigate] = useLocation();
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [creditMenuOpen, setCreditMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const langRef = useRef<HTMLDivElement | null>(null);
  const isCompact = useDirectorMobileViewport();
  const currentLangName =
    languageNames.find((l) => l.code === currentLang)?.name ?? "English";
  const chrome = chromeCopyFor(currentLang);
  const displayName =
    userName || user?.name || user?.email?.split("@")[0] || null;
  const displayImage = userImage || (user as any)?.image || null;
  const currentPlan = plan || "free";
  const showUpgradeButton = !!user && getPlanTierIndex(currentPlan) < 2;

  const openUpgrade = (intent: "upgrade" | "topup") => {
    if (onUpgradeClick) {
      onUpgradeClick(intent);
      return;
    }
    navigate(withLanguagePath("/subscription"));
  };

  const handleSignIn = useCallback(() => {
    if (onSignIn) {
      onSignIn();
      return;
    }
    const redirectPath =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : "/ai-music-video-generator";
    window.location.href = getLoginUrl(redirectPath);
  }, [onSignIn]);

  useEffect(() => {
    if (!profileOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node))
        setProfileOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [profileOpen]);

  useEffect(() => {
    if (!langOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node))
        setLangOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [langOpen]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: isCompact ? 2 : 6,
        color: "var(--fg-dim)",
        fontSize: isCompact ? 12 : 13,
      }}
    >
      {showUpgradeButton && (
        <button
          type="button"
          onClick={() => openUpgrade("upgrade")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            width: isCompact ? 32 : undefined,
            height: isCompact ? 32 : 28,
            padding: isCompact ? 0 : "0 12px",
            borderRadius: 8,
            border: "none",
            background: "var(--accent-2)",
            color: "#fff",
            fontFamily: "inherit",
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--accent)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--accent-2)";
          }}
        >
          <Crown size={13} aria-hidden="true" />
          {!isCompact && t("globalNav.nav.upgrade")}
        </button>
      )}
      <button
        type="button"
        aria-label={chrome.openFeedback}
        title={chrome.feedback}
        onClick={() => setFeedbackOpen(true)}
        style={{
          display: isCompact ? "none" : "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "transparent",
          border: "none",
          color: "inherit",
          cursor: "pointer",
          padding: 0,
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = "var(--panel-2)")
        }
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <HelpCircle size={16} />
      </button>
      {!isCompact && <DiscordButton />}
      <div
        ref={langRef}
        style={{ position: "relative", display: "flex", alignItems: "center" }}
      >
        <button
          type="button"
          aria-label={t("globalNav.nav.language")}
          aria-expanded={langOpen}
          onClick={() => setLangOpen((v) => !v)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            width: isCompact ? 32 : undefined,
            padding: isCompact ? 0 : "0 10px",
            height: 32,
            borderRadius: 8,
            background: "transparent",
            border: "none",
            color: "inherit",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 13,
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "var(--panel-2)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "transparent")
          }
        >
          <Globe size={14} />
          {!isCompact && <span>{currentLangName}</span>}
        </button>
        {langOpen && (
          <div
            role="menu"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              minWidth: 180,
              maxHeight: 360,
              overflowY: "auto",
              background: "var(--panel)",
              border: "1px solid var(--line-2)",
              borderRadius: 8,
              padding: 4,
              boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
              zIndex: 50,
            }}
          >
            {languageNames.map(({ code, name }) => {
              const active = code === currentLang;
              return (
                <button
                  key={code}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    changeLanguage(code);
                    setLangOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "8px 10px",
                    background: active ? "var(--panel-2)" : "transparent",
                    border: "none",
                    borderRadius: 4,
                    color: "var(--fg)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--panel-2)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = active
                      ? "var(--panel-2)"
                      : "transparent")
                  }
                >
                  <span>{name}</span>
                  {active && (
                    <span style={{ color: "var(--accent)", fontSize: 12 }}>
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {credits != null && (
        <div
          onMouseEnter={() => setCreditMenuOpen(true)}
          onMouseLeave={() => setCreditMenuOpen(false)}
          style={{ position: "relative", display: "flex" }}
        >
          <button
            type="button"
            onClick={() => openUpgrade("upgrade")}
            title={t("globalNav.nav.creditsTitle")}
            aria-label={`${credits} ${t("globalNav.nav.creditsTitle")}. ${t("globalNav.nav.openUpgradeOptions")}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "var(--fg)",
              fontWeight: 600,
              background: "transparent",
              border: "none",
              padding: isCompact ? "0 6px" : "0 10px",
              height: 32,
              borderRadius: 8,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--panel-2)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <Star
              size={14}
              fill="currentColor"
              style={{ color: "var(--fg-dim)" }}
            />
            <span style={{ fontFamily: "var(--mono)" }}>
              {(credits ?? 0).toLocaleString()}
            </span>
          </button>
          {creditMenuOpen && (
            <div
              role="menu"
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                width: 260,
                maxWidth: "calc(100vw - 16px)",
                paddingTop: 12,
                zIndex: 100,
              }}
            >
              <div
                style={{
                  padding: 10,
                  background: "#202029",
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderRadius: 14,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.52)",
                }}
              >
              <div
                style={{
                  padding: "8px 14px 10px",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  marginBottom: 4,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 13.5,
                    color: "rgba(255,255,255,0.72)",
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Star
                      size={13}
                      fill="currentColor"
                      style={{ color: "rgba(255,255,255,0.4)" }}
                    />
                    {t("globalNav.nav.generalCredits", {
                      defaultValue: "General credits",
                    })}
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--mono)",
                      fontWeight: 600,
                      color: "#fff",
                    }}
                  >
                    {(credits ?? 0).toLocaleString()}
                  </span>
                </div>
                {(musicVideoCredits ?? 0) > 0 && (
                  <>
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: 13.5,
                        color: "rgba(255,255,255,0.72)",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <VideoIcon size={13} style={{ color: "#a5b4fc" }} />
                        {t("globalNav.nav.musicVideoCredits", {
                          defaultValue: "Music video credits",
                        })}
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--mono)",
                          fontWeight: 600,
                          color: "#fff",
                        }}
                      >
                        {(musicVideoCredits ?? 0).toLocaleString()}
                      </span>
                    </div>
                    <p
                      style={{
                        margin: "3px 0 0 21px",
                        fontSize: 11.5,
                        lineHeight: 1.4,
                        color: "rgba(255,255,255,0.45)",
                      }}
                    >
                      {t("globalNav.nav.musicVideoCreditsNote", {
                        defaultValue:
                          "New-user starter credits for music videos only.",
                      })}
                    </p>
                  </>
                )}
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setCreditMenuOpen(false);
                  openUpgrade("topup");
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  width: "100%",
                  padding: "12px 14px",
                  border: "none",
                  borderRadius: 12,
                  background: "transparent",
                  color: "#fff",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 15,
                  fontWeight: 500,
                  textAlign: "left",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.08)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <ShoppingBag size={20} aria-hidden="true" />
                {t("globalNav.nav.addOnCredits")}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setCreditMenuOpen(false);
                  setCreditsOpen(true);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  width: "100%",
                  marginTop: 4,
                  padding: "12px 14px",
                  border: "none",
                  borderRadius: 12,
                  background: "transparent",
                  color: "#fff",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 15,
                  fontWeight: 500,
                  textAlign: "left",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.08)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <ReceiptText size={20} aria-hidden="true" />
                {t("globalNav.nav.creditUsageDetails")}
              </button>
              </div>
            </div>
          )}
        </div>
      )}
      {!isCompact && <Bell size={17} />}
      {user ? (
        <div
          ref={profileRef}
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            aria-label={t("globalNav.nav.accountMenu")}
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              background: "transparent",
              border: "none",
              padding: 0,
              borderRadius: 999,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              color: "inherit",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                overflow: "hidden",
                border: "1px solid var(--line-2)",
                flexShrink: 0,
                background: "var(--panel)",
              }}
            >
              {displayImage ? (
                <img
                  src={displayImage}
                  alt={displayName || t("globalNav.nav.accountMenu")}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "grid",
                    placeItems: "center",
                    color: "var(--fg-mute)",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {(displayName || "A").charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </button>
          {profileOpen && (
            <div
              role="menu"
              style={{
                position: "absolute",
                top: "calc(100% + 10px)",
                right: 0,
                minWidth: 240,
                padding: 6,
                background: "var(--panel)",
                border: "1px solid var(--line-2)",
                borderRadius: 12,
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                zIndex: 100,
              }}
            >
              <div
                style={{
                  padding: "10px 12px 8px",
                  borderBottom: "1px solid var(--line-2)",
                  marginBottom: 4,
                }}
              >
                {user?.name && (
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: "var(--fg)",
                      marginBottom: 2,
                    }}
                  >
                    {user.name}
                  </div>
                )}
                {user?.email && (
                  <div style={{ fontSize: 12, color: "var(--fg-dim)" }}>
                    {user.email}
                  </div>
                )}
              </div>
              <MenuItem
                icon={<FolderOpen size={15} />}
                label={t("globalNav.nav.myProjects", {
                  defaultValue: "My Projects",
                })}
                onClick={() => {
                  setProfileOpen(false);
                  navigate(withLanguagePath("/projects"));
                }}
              />
              <MenuItem
                icon={<UserIcon size={15} />}
                label={t("globalNav.nav.myProfile")}
                onClick={() => {
                  setProfileOpen(false);
                  if (user?.id)
                    navigate(withLanguagePath(`/author/${user.id}`));
                }}
              />
              <MenuItem
                icon={<Settings size={15} />}
                label={t("globalNav.nav.manageSubscription")}
                onClick={() => {
                  setProfileOpen(false);
                  navigate(withLanguagePath("/subscription"));
                }}
              />
              <div
                style={{
                  height: 1,
                  background: "var(--line-2)",
                  margin: "4px 0",
                }}
              />
              <MenuItem
                icon={<LogOut size={15} />}
                label={t("globalNav.nav.logout")}
                onClick={async () => {
                  setProfileOpen(false);
                  await logout();
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={handleSignIn}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: 32,
            padding: "0 14px",
            borderRadius: 999,
            border: "1px solid rgba(167,139,250,0.38)",
            background: "rgba(167,139,250,0.12)",
            color: "var(--fg)",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "rgba(167,139,250,0.2)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "rgba(167,139,250,0.12)")
          }
        >
          {t("globalNav.nav.signIn")}
        </button>
      )}
      {rightSlot}
      <CreditHistoryModal
        open={creditsOpen}
        onClose={() => setCreditsOpen(false)}
      />
      <FeedbackDialog
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        hideTrigger
      />
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void | Promise<void>;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "9px 12px",
        borderRadius: 8,
        fontSize: 13.5,
        color: "var(--fg)",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = "var(--panel-2)")
      }
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <span style={{ color: "var(--fg-dim)", display: "inline-flex" }}>
        {icon}
      </span>
      {label}
    </button>
  );
}
